'use client';

import { Search } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Radio,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Image from 'next/image';

import { useFetchNetworksQuery } from '@/store/services/network.service';

interface FilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  sortValue: string;
  onSortChange: (value: string) => void;
  networkValues: string[];
  onNetworkChange: (values: string[]) => void;
}

const SORT_OPTIONS = [
  { value: '{"price": "desc"}', label: 'Highest to lowest' },
  { value: '{"price": "asc"}', label: 'Lowest to highest' },
];

const MenuProps = {
  PaperProps: {
    sx: {
      background: '#060912',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '0px',
    },
  },
};

export const FilterBar = ({
  searchValue,
  onSearchChange,
  sortValue,
  onSortChange,
  networkValues,
  onNetworkChange,
}: FilterBarProps) => {
  const { data: networksData, isLoading: isLoadingNetworks } = useFetchNetworksQuery();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleNetworkChange = (event: SelectChangeEvent<typeof networkValues>) => {
    const {
      target: { value },
    } = event;
    onNetworkChange(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent='flex-end'
      gap={{ xs: 2, sm: 2, md: 3 }}
      width='100%'
      sx={{
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
      }}>
      <TextField
        placeholder='Search...'
        value={searchValue}
        onChange={e => onSearchChange(e.target.value)}
        variant='outlined'
        size={isMobile ? 'small' : 'medium'}
        InputProps={{
          startAdornment: <Search sx={{ color: 'rgba(255, 255, 255, 0.35)', fontSize: { xs: 20, sm: 24 }, mr: 1 }} />,
        }}
        sx={{
          width: { xs: '100%', lg: '320px' },
          maxWidth: { xs: '100%', lg: '400px' },
          '& .MuiOutlinedInput-root': {
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: '0px',
            color: 'white',
            '& fieldset': {
              border: 'none',
            },
            '&:hover fieldset': {
              border: 'none',
            },
            '&.Mui-focused fieldset': {
              border: '1px solid rgba(255, 255, 255, 0.2)',
            },
            '& input::placeholder': {
              color: 'rgba(255, 255, 255, 0.3)',
              opacity: 1,
            },
          },
        }}
      />

      <Stack
        direction={{ xs: 'row', sm: 'row' }}
        gap={{ xs: 1, sm: 1.5, md: 2 }}
        sx={{
          width: { xs: '100%', lg: 'auto' },
          flexWrap: 'nowrap',
          justifyContent: { xs: 'space-between', sm: 'flex-start' },
        }}>
        <FormControl
          size={isMobile ? 'small' : 'medium'}
          sx={{
            minWidth: { xs: '48%', sm: '140px' },
            width: { xs: '48%', sm: 'auto' },
          }}>
          <Select
            value={sortValue}
            onChange={e => onSortChange(e.target.value)}
            displayEmpty
            renderValue={value => {
              const option = SORT_OPTIONS.find(opt => opt.value === value);
              return option ? option.label : 'Sort by';
            }}
            sx={{
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              borderRadius: '0px',
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: '1px solid rgba(255, 255, 255, 0.2)',
              },
              '& .MuiSelect-icon': {
                color: 'rgba(255, 255, 255, 0.35)',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  background: '#060912',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '0px',
                  mt: 1,
                },
              },
              disableScrollLock: true,
            }}>
            {SORT_OPTIONS.map(option => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.05)',
                  },
                  '&.Mui-selected': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.15)',
                    },
                  },
                }}>
                <span>{option.label}</span>
                <Radio
                  checked={sortValue === option.value}
                  sx={{
                    color: 'white',
                    '&.Mui-checked': {
                      color: 'white',
                    },
                  }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size={isMobile ? 'small' : 'medium'}
          sx={{
            minWidth: { xs: '48%', sm: '140px' },
            width: { xs: '48%', sm: 'auto' },
          }}>
          <Select
            multiple
            displayEmpty
            value={networkValues}
            onChange={handleNetworkChange}
            input={<OutlinedInput />}
            renderValue={selected => {
              if (selected.length === 0) {
                return <span style={{ color: 'rgba(255, 255, 255, 0.3)' }}>Network</span>;
              }
              if (selected.length === 1) {
                const network = networksData?.data?.find(n => n.id === selected[0]);
                return network?.name || 'Network';
              }
              return `${selected.length} networks selected`;
            }}
            inputProps={{ 'aria-label': 'Without label' }}
            sx={{
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              borderRadius: '0px',
              color: 'white',
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: '1px solid rgba(255, 255, 255, 0.2)',
              },
              '& .MuiSelect-icon': {
                color: 'rgba(255, 255, 255, 0.35)',
              },
            }}
            MenuProps={{
              ...MenuProps,
              disableScrollLock: true,
              PaperProps: {
                ...MenuProps.PaperProps,
                sx: {
                  ...MenuProps.PaperProps.sx,
                  mt: 1,
                },
              },
            }}>
            <MenuItem disabled value=''>
              <span style={{ color: 'rgba(255, 255, 255, 0.3)' }}>Network</span>
            </MenuItem>
            {isLoadingNetworks ? (
              <MenuItem disabled>
                <Typography color='white'>Loading networks...</Typography>
              </MenuItem>
            ) : (
              networksData?.data?.map(network => (
                <MenuItem
                  key={network.id}
                  value={network.id}
                  sx={{
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.05)',
                    },
                    '&.Mui-selected': {
                      background: 'rgba(255, 255, 255, 0.1)',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.15)',
                      },
                    },
                    justifyContent: 'space-between',
                  }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                    <Image
                      src={network.image}
                      alt={network.name}
                      width={20}
                      height={20}
                      style={{ width: '20px', height: '20px' }}
                    />
                    <ListItemText primary={network.name} />
                  </Box>
                  <Checkbox
                    checked={networkValues.indexOf(network.id) > -1}
                    sx={{
                      color: 'white',
                      '&.Mui-checked': {
                        color: 'white',
                      },
                    }}
                  />
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
};
